import { ContentState } from "draft-js";
import { ContentType } from "@domain/content/contentType";
import { GroupMemberRole } from "@domain/group/group";
import { ContentLicense } from "@domain/licence/contentLicense";
import { OrganizationMemberRole } from "@domain/organization/organization";
import { OrganizationType } from "@domain/organization/organizationType";
import { ReportMotive } from "@domain/report/report";
import { Person } from "@domain/user/user";

export type NotificationType =
	| "NEW_POST_MENTION"
	| "NEW_COURSE"
	| "NEW_ARTICLE"
	| "NEW_CAROUSEL"
	| "LIVE_COURSE_STARTED"
	| "NEW_LIVE_COURSE"
	| "NEW_FOLLOWER"
	| "NEW_POST_LIKE"
	| "NEW_POST_COMMENT"
	| "NEW_POST_SHARE"
	| "NEW_COURSE_FEEDBACK"
	| "NEW_ARTICLE_FEEDBACK"
	| "USER_LINKED_TO_INSTITUTE"
	| "ORGANIZATION_DELETED"
	| "COURSE_DELETED"
	| "ARTICLE_DELETED"
	| "NEW_QUIZ"
	| "QUIZ_PARTICIPATION"
	| "USER_INVITED_TO_SHAREROOM"
	| "USER_ACCEPTED_TO_SHAREROOM"
	| "USER_REMOVED_FROM_SHAREROOM"
	| "ACCESS_REQUESTED_FOR_SHAREROOM"
	| "ACCESS_GRANTED_FOR_SHAREROOM"
	| "USER_INVITED_TO_GROUP"
	| "USER_ACCEPTED_TO_GROUP"
	| "USER_REMOVED_FROM_GROUP"
	| "ACCESS_REQUESTED_FOR_GROUP"
	| "ACCESS_GRANTED_FOR_GROUP"
	| "CONTENT_MODERATED"
	| "NEW_CAROUSEL_INSTRUCTIVE"
	| "NEW_LIVE_INSTANT_COURSE"
	| "NEW_LIVE_SCHEDULE_COURSE"
	| "NEW_APPLICATION_POST_VIDEO"
	| "NEW_USER_JOIN_TO_SHAREROOM"
	| "USER_LEFT_SHAREROOM"
	| "NEW_SHAREROOM_POST"
	| "NEW_SHAREROOM_QUIZ"
	| "NEW_SHAREROOM_COURSE"
	| "NEW_SHAREROOM_ARTICLE"
	| "NEW_PREMIUM_SHAREROOM"
	| "NEW_GROUP_FEEDBACK";

export interface NotificationBase {
	id: string;
	date: Date;
	read: boolean;
	source?: Person;
	//liveCourse?: NotificationStartedLiveCourse;
	type: NotificationType;
}

export interface NotificationPostPayload {
	type: "NEW_POST_MENTION";
	post: NotificationPost;
	source: Person;
	organization?: Person;
}
export interface NotificationCoursePayload {
	type: "NEW_COURSE";
	course: NotificationCourse;
	source: Person;
}
// export interface NotificationLiveCreatedPayload {
// 	type: "LIVE_COURSE_STARTED";
// 	livecourse: NotificationLiveCreated;
// 	source: Person;
// }
export interface NotificationStartedLiveCoursePayload {
	type: "LIVE_COURSE_STARTED";
	liveCourse: NotificationStartedLiveCourse;
	source: Person;
}

// export interface NewLiveCoursePayload {
// 	type: "NEW_LIVE_COURSE";
// 	livecourse: NewLiveCourse;
// 	source: Person;
// }

export interface NewLiveInstantCoursePayload {
	type: "NEW_LIVE_INSTANT_COURSE";
	liveCourse: NewLiveCourse;
	source: Person;
}

export interface NewLiveScheduleCoursePayload {
	type: "NEW_LIVE_SCHEDULE_COURSE";
	liveCourse: NewLiveCourse;
	source: Person;
}

export interface NotificationArticlePayload {
	type: "NEW_ARTICLE";
	article: NotificationArticle;
	source: Person;
}

export interface NotificationCarouselPayload {
	type: "NEW_CAROUSEL";
	carousel: NotificationCarousel;
	source: Person;
}

export interface NotificationCarouselInstructivePayload {
	type: "NEW_CAROUSEL_INSTRUCTIVE";
	carousel: NotificationCarouselInstructive;
	source: Person;
}

export interface NotificationFollowerPayload {
	type: "NEW_FOLLOWER";
	source: Person;
}
export interface NotificationNewPostLikePayload {
	type: "NEW_POST_LIKE";
	author: NotificationUser;
	post: NotificationPost;
	source: Person;
	organization?: Person;
}
export interface NotificationNewPostSharePayload {
	type: "NEW_POST_SHARE";
	author: NotificationUser;
	post: NotificationPost;
	source: Person;
	organization?: Person;
}

export interface NotificationNewPostShareApplicationPayload {
	type: "NEW_APPLICATION_POST_VIDEO";
	author: NotificationUser;
	post: NotificationPostShareApplication;
	source: Person;
}
export interface NotificationNewPostCommentPayload {
	type: "NEW_POST_COMMENT";
	author: NotificationUser;
	post: NotificationPost;
	comment: NotificationPostComment;
	source: Person;
	organization?: Person;
}
export interface NotificationNewCourseFeedbackPayload {
	type: "NEW_COURSE_FEEDBACK";
	course: NotificationCourse;
	feedback: NotificationCourseFeedback;
	source: Person;
}

export interface NotificationNewArticleFeedbackPayload {
	type: "NEW_ARTICLE_FEEDBACK";
	article: NotificationArticle;
	feedback: NotificationArticleFeedback;
	source: Person;
}

export interface NotificationLinkedInstitutePayload {
	type: "USER_LINKED_TO_INSTITUTE";
	institute: NotificationInstititute;
	role: OrganizationMemberRole;
	source: Person;
}
export interface NotificationOrganizationRemovedPayload {
	type: "ORGANIZATION_DELETED";
	source: Person;
	organization: NotificationOrganization;
}
export interface NotificationCourseDeletedPayload {
	type: "COURSE_DELETED";
	course: NotificationCourse;
	byUser: NotificationUser;
	institute: NotificationInstititute;
	source: Person;
}

export interface NotificationArticleDeletedPayload {
	type: "ARTICLE_DELETED";
	article: NotificationArticle;
	byUser: NotificationUser;
	institute: NotificationInstititute;
	source: Person;
}

export interface NotificationNewQuizPayload {
	type: "NEW_QUIZ";
	quiz: NotificationQuiz;
	source: Person;
}
export interface NotificationNewQuizParticipationPayload {
	type: "QUIZ_PARTICIPATION";
	quiz: NotificationQuiz;
	source: Person;
}
export interface NotificationUserInvitedToShareroomPayload {
	type: "USER_INVITED_TO_SHAREROOM" | "USER_INVITED_TO_GROUP";
	group: NotificationGroup;
	role: GroupMemberRole;
	source: Person;
}
export interface NotificationUserAcceptedToShareroomPayload {
	type: "USER_ACCEPTED_TO_SHAREROOM" | "USER_ACCEPTED_TO_GROUP";
	group: NotificationGroup;
	role: GroupMemberRole;
	source: Person;
}
export interface NotificationUserRemovedFromShareroomPayload {
	type: "USER_REMOVED_FROM_SHAREROOM" | "USER_REMOVED_FROM_GROUP";
	group: NotificationGroup;
	role: GroupMemberRole;
	source: Person;
}
export interface NotificationAccessRequestedForShareroomPayload {
	type: "ACCESS_REQUESTED_FOR_SHAREROOM" | "ACCESS_REQUESTED_FOR_GROUP";
	group: NotificationGroup;
	source: Person;
}
export interface NotificationAccessGrantedForShareroomPayload {
	type: "ACCESS_GRANTED_FOR_SHAREROOM" | "ACCESS_GRANTED_FOR_GROUP";
	group: NotificationGroup;
	role: GroupMemberRole;
	source: Person;
}

export interface NotificationUserSubscriberToShareroomPayload {
	type: "NEW_USER_JOIN_TO_SHAREROOM";
	group: NotificationGroup;
	role: GroupMemberRole;
	source: Person;
}

export interface NotificationUserUnsubscriberToShareroomPayload {
	type: "USER_LEFT_SHAREROOM";
	group: NotificationGroup;
	role: GroupMemberRole;
	source: Person;
}

export interface NotificationUserPublishPostToShareroomPayload {
	type: "NEW_SHAREROOM_POST";
	group: NotificationGroup;
	role: GroupMemberRole;
	source: Person;
	post: NotificationPost;
}

export interface NotificationUserShareContentQuizToShareroomPayload {
	type: "NEW_SHAREROOM_QUIZ";
	group: NotificationGroup;
	role: GroupMemberRole;
	source: Person;
	quiz: NotificationQuiz;
}

export interface NotificationUserShareContentCourseToShareroomPayload {
	type: "NEW_SHAREROOM_COURSE";
	group: NotificationGroup;
	role: GroupMemberRole;
	source: Person;
	course: NotificationCourse;
}

export interface NotificationUserPremiumToShareroomPayload {
	type: "NEW_PREMIUM_SHAREROOM";
	group: NotificationGroup;
	role: GroupMemberRole;
	source: Person;
}

export interface NotificationUserShareContentArticleToShareroomPayload {
	type: "NEW_SHAREROOM_ARTICLE";
	group: NotificationGroup;
	role: GroupMemberRole;
	source: Person;
	article: NotificationArticle;
}

export interface NotificationUserToShareroomFeedbackPayload {
	type: "NEW_GROUP_FEEDBACK";
	group: NotificationGroup;
	role: GroupMemberRole;
	source: Person;
	feedback: NotificationShareroomFeedback;
}

export interface NotificationContentModeratedPayload {
	type: "CONTENT_MODERATED";
	motive: ReportMotive;
	content: NotificationContent;
}

export type NotificationPayload =
	| NotificationPostPayload
	| NotificationCarouselPayload
	| NotificationCoursePayload
	| NotificationArticlePayload
	// | NotificationLiveCreatedPayload
	| NotificationStartedLiveCoursePayload
	| NotificationFollowerPayload
	| NewLiveInstantCoursePayload
	| NewLiveScheduleCoursePayload
	| NotificationNewPostLikePayload
	| NotificationNewPostSharePayload
	| NotificationNewPostCommentPayload
	| NotificationNewCourseFeedbackPayload
	| NotificationNewArticleFeedbackPayload
	| NotificationLinkedInstitutePayload
	| NotificationOrganizationRemovedPayload
	| NotificationCourseDeletedPayload
	| NotificationArticleDeletedPayload
	| NotificationNewQuizPayload
	| NotificationNewQuizParticipationPayload
	| NotificationUserInvitedToShareroomPayload
	| NotificationUserAcceptedToShareroomPayload
	| NotificationUserRemovedFromShareroomPayload
	| NotificationAccessRequestedForShareroomPayload
	| NotificationAccessGrantedForShareroomPayload
	| NotificationUserSubscriberToShareroomPayload
	| NotificationUserUnsubscriberToShareroomPayload
	| NotificationContentModeratedPayload
	| NotificationCarouselInstructivePayload
	| NotificationNewPostShareApplicationPayload
	| NotificationUserPublishPostToShareroomPayload
	| NotificationUserShareContentQuizToShareroomPayload
	| NotificationUserShareContentCourseToShareroomPayload
	| NotificationUserShareContentArticleToShareroomPayload
	| NotificationUserPremiumToShareroomPayload
	| NotificationUserToShareroomFeedbackPayload;

export type TooshareNotification = NotificationBase & NotificationPayload;

export interface NotificationPost {
	id: string;
	text?: ContentState;
	assetCount: number;
}
export interface NotificationPostComment {
	id: string;
	text: ContentState;
}
export interface NotificationCourse {
	id: string;
	title: string;
}

export interface NotificationPostShareApplication {
	id: string;
	text?: ContentState;
}
// export interface NotificationLiveCreated {
// 	id: string;
// 	link: string;
// 	linkType: string;
// 	title: string;
// 	workspaceId: string;
// 	participationLink: string;
// 	endDate: Date;
// 	startDate: Date;
// }

export interface NotificationStartedLiveCourse {
	id: string;
	link: string;
	linkType: string;
	title: string;
	workspaceId: string;
	participationLink: string;
	endDate: Date;
	startDate: Date;
	status: string;
}

export interface NewLiveCourse {
	id: string;
	link: string;
	linkType: string;
	title: string;
	workspaceId: string;
	participationLink: string;
	endDate: Date;
	startDate: Date;
	status: string;
}

export interface NotificationArticle {
	id: string;
	title: string;
}

export interface NotificationCarousel {
	id: string;
	title: string;
	category: string;
	slug: string;
}

export interface NotificationCarouselInstructive {
	id: string;
	title: string;
	carouselId: string;
	category: string;
	slug: string;
}

export interface NotificationUser {
	id: string;
	primaryName: string;
	secondaryName?: string;
	avatarUrl?: string;
}
export interface NotificationCourseFeedback {
	id: string;
	score: number;
	text?: string;
}

export interface NotificationArticleFeedback {
	id: string;
	score: number;
	text?: string;
}

export interface NotificationInstititute {
	id: string;
	primaryName: string;
	avatarUrl?: string;
	type: "INSTITUTE";
}
export interface NotificationOrganization {
	id: string;
	primaryName: string;
	avatarUrl?: string;
	type: OrganizationType;
}
export interface NotificationQuiz {
	id: string;
	title: string;
}
export interface NotificationGroup {
	id: string;
	name: string;
	license?: ContentLicense;
}
export interface NotificationShareroomFeedback {
	id: string;
	score: number;
	text?: string;
}

export const notificationCategories = {
	FOLLOWUPS: "FOLLOWUPS",
	MENTIONS: "MENTIONS",
	SHARING: "SHARING",
	LIKES: "LIKES",
	COMMENTS: "COMMENTS",
	PUBLICATIONS: "PUBLICATIONS",
	FEEDBACKS: "FEEDBACKS",
	PARTICIPATIONS: "PARTICIPATIONS",
	ORGANIZATIONS: "ORGANIZATIONS",
	DELETIONS: "DELETIONS",
	SHAREROOMS: "SHAREROOMS",
	GROUPS: "GROUPS",
	INSTRUCTIVE: "INSTRUCTIVE",
	LIVE_COURSE: "LIVE_COURSE",
	SYSTEM: "SYSTEM",
};

export type NotificationCategory = keyof typeof notificationCategories;
export const allNotificationSettingsCategories = Object.keys(notificationCategories) as NotificationCategory[];

export type NotificationMode = "INAPP" | "EMAIL";

export interface NotificationFilter {
	category: NotificationCategory;
	mode: NotificationMode;
}

export interface NotificationContent {
	type: ContentType;
	name: string;
}
